import React,{useState,useEffect} from 'react';
import {supabase} from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiEdit,FiTrash2,FiPlus,FiSave,FiX,FiLoader,FiDollarSign,
  FiFire,FiCheck,FiAlertCircle,FiArrowUp,FiArrowDown,
  FiChevronDown,FiChevronRight,FiMenu
}=FiIcons;

const MenuOptionsAdmin=()=> {
  const [activeProductTab,setActiveProductTab]=useState('burger');// 'burger' or 'pizza'
  const [menuOptions,setMenuOptions]=useState({
    burger: {
      patty: [],
      bun: [],
      topping: [],
      sauce: [],
      side: [],
      spicy_level: []
    },
    pizza: {
      base: [],
      topping: [],
      sauce: [],
      side: [],
      spicy_level: []
    }
  });

  const [loading,setLoading]=useState(true);
  const [editingItem,setEditingItem]=useState(null);
  const [newItem,setNewItem]=useState(null);
  const [activeTab,setActiveTab]=useState('patty');
  const [message,setMessage]=useState(null);
  const [deleting,setDeleting]=useState(null);
  const [deletedItems,setDeletedItems]=useState(new Set());

  // Load menu options from database
  useEffect(()=> {
    fetchMenuOptions();
  },[activeProductTab]);

  const fetchMenuOptions=async ()=> {
    setLoading(true);
    try {
      const {data,error}=await supabase
        .from('menu_options_hb47x')
        .select('*')
        .eq('product_type',activeProductTab)
        .order('display_order',{ascending: true});

      if (error) {
        console.error('Error fetching menu options:',error);
        setMessage({type: 'error',text: 'Fehler beim Laden der Menüoptionen.'});
        return;
      }
      // Filter out locally deleted items to prevent them from reappearing
      const filteredData=data.filter(item=> !deletedItems.has(item.id));

      // Group data by option_type
      const grouped=filteredData.reduce((acc,item)=> {
        if (!acc[item.option_type]) {
          acc[item.option_type]=[];
        }
        acc[item.option_type].push(item);
        return acc;
      },{});

      const currentStructure=activeProductTab==='burger'
        ? {
          patty: grouped.patty || [],
          bun: grouped.bun || [],
          topping: grouped.topping || [],
          sauce: grouped.sauce || [],
          side: grouped.side || [],
          spicy_level: grouped.spicy_level || []
        }
        : {
          base: grouped.base || [],
          topping: grouped.topping || [],
          sauce: grouped.sauce || [],
          side: grouped.side || [],
          spicy_level: grouped.spicy_level || []
        };
      setMenuOptions(prev=> ({...prev,[activeProductTab]: currentStructure}));
      console.log(`Menu options loaded for ${activeProductTab} (filtered):`,currentStructure);
    } catch (error) {
      console.error('Failed to fetch menu options:',error);
      setMessage({type: 'error',text: 'Fehler beim Laden der Menüoptionen.'});
    } finally {
      setLoading(false);
    }
  };

  const getCurrentOptions=()=> menuOptions[activeProductTab];
  const getCurrentOptionTypes=()=> Object.keys(getCurrentOptions());

  const handleAddNew=(type)=> {
    setNewItem({
      product_type: activeProductTab,
      option_type: type,
      item_id: '',
      name: '',
      price: type==='spicy_level' ? 0 : 1.0,
      is_spicy: false,
      is_active: true,
      display_order: getCurrentOptions()[type].length + 1
    });
  };

  const handleEdit=(item)=> {
    setEditingItem({...item});
  };

  const handleCancelEdit=()=> {
    setEditingItem(null);
    setNewItem(null);
  };

  const handleChange=(e,item)=> {
    const {name,value,type,checked}=e.target;
    const updatedItem={...item};
    if (type==='checkbox') {
      updatedItem[name]=checked;
    } else if (name==='price') {
      updatedItem[name]=parseFloat(value) || 0;
    } else {
      updatedItem[name]=value;
    }

    if (editingItem) {
      setEditingItem(updatedItem);
    } else if (newItem) {
      setNewItem(updatedItem);
    }
  };

  const handleSave=async (item)=> {
    try {
      setLoading(true);
      // Make sure item_id is set for new items
      if (item===newItem && !item.item_id) {
        // Generate item_id from name if empty
        item.item_id=item.name
          .toLowerCase()
          .replace(/[^a-z0-9]/g,'-')
          .replace(/-+/g,'-')
          .replace(/^-|-$/g,'');
      }
      // Make sure price is a number
      item.price=parseFloat(item.price) || 0;

      // Update or insert the item
      let result;
      if (item.id) {
        // Update existing item
        result=await supabase
          .from('menu_options_hb47x')
          .update({
            name: item.name,
            price: item.price,
            is_spicy: item.is_spicy,
            is_active: item.is_active,
            display_order: item.display_order,
            updated_at: new Date().toISOString()
          })
          .eq('id',item.id);
      } else {
        // Insert new item
        result=await supabase
          .from('menu_options_hb47x')
          .insert({
            product_type: item.product_type,
            option_type: item.option_type,
            item_id: item.item_id,
            name: item.name,
            price: item.price,
            is_spicy: item.is_spicy,
            is_active: item.is_active,
            display_order: item.display_order
          });
      }

      if (result.error) {
        throw result.error;
      }
      setMessage({type: 'success',text: 'Option erfolgreich gespeichert!'});
      setEditingItem(null);
      setNewItem(null);
      // Refresh data immediately
      await fetchMenuOptions();
    } catch (error) {
      console.error('Error saving menu option:',error);
      setMessage({type: 'error',text: `Fehler beim Speichern: ${error.message}${error.details ? ` (${error.details})` : ''}`});
    } finally {
      setLoading(false);
    }
  };

  const handleDelete=async (item)=> {
    if (!confirm(`Sind Sie sicher,dass Sie "${item.name}" löschen möchten?`)) {
      return;
    }
    try {
      setDeleting(item.id);
      console.log('Deleting menu option with ID:',item.id);
      // SCHRITT 1: Sofort aus dem lokalen State entfernen
      setMenuOptions(prev=> {
        const newMenuOptions={...prev};
        newMenuOptions[activeProductTab][item.option_type]=prev[activeProductTab][item.option_type].filter(i=> i.id !==item.id);
        console.log('Immediately removed from local state:',newMenuOptions);
        return newMenuOptions;
      });
      // SCHRITT 2: Zur Liste der gelöschten Items hinzufügen
      setDeletedItems(prev=> {
        const newSet=new Set(prev);
        newSet.add(item.id);
        console.log('Added to deleted items list:',item.id);
        return newSet;
      });
      // SCHRITT 3: Aus der Datenbank löschen
      const {error}=await supabase
        .from('menu_options_hb47x')
        .delete()
        .eq('id',item.id);

      if (error) {
        console.error('Database delete error:',error);
        // Bei Fehler: Element wieder hinzufügen
        setDeletedItems(prev=> {
          const newSet=new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
        await fetchMenuOptions();// Reload to restore state
        throw error;
      }
      console.log('Successfully deleted from database:',item.id);
      setMessage({type: 'success',text: `"${item.name}" erfolgreich gelöscht!`});
    } catch (error) {
      console.error('Error deleting menu option:',error);
      setMessage({type: 'error',text: `Fehler beim Löschen: ${error.message}`});
    } finally {
      setDeleting(null);
    }
  };

  const handleMoveItem=async (item,direction)=> {
    try {
      setLoading(true);
      // Find the other item to swap with
      const itemList=[...getCurrentOptions()[item.option_type]];
      const currentIndex=itemList.findIndex(i=> i.id===item.id);
      const newIndex=direction==='up' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex < 0 || newIndex >=itemList.length) {
        return;// Can't move further
      }
      const otherItem=itemList[newIndex];

      // Swap display orders
      const itemOrder=item.display_order;
      const otherOrder=otherItem.display_order;

      // Update both items
      const updates=[
        supabase
          .from('menu_options_hb47x')
          .update({display_order: otherOrder,updated_at: new Date().toISOString()})
          .eq('id',item.id),
        supabase
          .from('menu_options_hb47x')
          .update({display_order: itemOrder,updated_at: new Date().toISOString()})
          .eq('id',otherItem.id)
      ];
      await Promise.all(updates);
      fetchMenuOptions();
    } catch (error) {
      console.error('Error moving menu option:',error);
      setMessage({type: 'error',text: `Fehler beim Verschieben: ${error.message}`});
    } finally {
      setLoading(false);
    }
  };

  const renderItemRow=(item)=> {
    const isEditing=editingItem && editingItem.id===item.id;
    const itemList=getCurrentOptions()[item.option_type];
    const isFirst=itemList.indexOf(item)===0;
    const isLast=itemList.indexOf(item)===itemList.length - 1;
    const isDeleting=deleting===item.id;
    return (
      <tr key={item.id} className={`${!item.is_active ? 'bg-gray-50 text-gray-500' : 'bg-white'} ${isDeleting ? 'opacity-50' : ''}`}>
        {isEditing ? (
          // Edit mode
          <>
            <td className="px-4 py-3">
              <input
                type="text"
                name="name"
                value={editingItem.name}
                onChange={(e)=> handleChange(e,editingItem)}
                className="w-full px-2 py-1 border border-gray-300 rounded"
              />
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center">
                <span className="mr-1">CHF</span>
                <input
                  type="number"
                  name="price"
                  value={editingItem.price}
                  onChange={(e)=> handleChange(e,editingItem)}
                  step="0.1"
                  min="0"
                  className="w-20 px-2 py-1 border border-gray-300 rounded"
                />
              </div>
            </td>
            {item.option_type==='sauce' && (
              <td className="px-4 py-3 text-center">
                <input
                  type="checkbox"
                  name="is_spicy"
                  checked={editingItem.is_spicy}
                  onChange={(e)=> handleChange(e,editingItem)}
                  className="w-5 h-5 rounded text-orange-500 focus:ring-orange-500"
                />
              </td>
            )}
            <td className="px-4 py-3 text-center">
              <input
                type="checkbox"
                name="is_active"
                checked={editingItem.is_active}
                onChange={(e)=> handleChange(e,editingItem)}
                className="w-5 h-5 rounded text-green-500 focus:ring-green-500"
              />
            </td>
            <td className="px-4 py-3">
              <div className="flex space-x-2">
                <button onClick={()=> handleSave(editingItem)} className="p-1 text-green-600 hover:text-green-800" disabled={loading}>
                  <SafeIcon icon={loading ? FiLoader : FiSave} className={loading ? 'animate-spin' : ''} />
                </button>
                <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:text-gray-800">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </td>
          </>
        ) : (
          // View mode
          <>
            <td className="px-4 py-3">
              <div className="flex items-center">
                <span className="font-medium">{item.name}</span>
                {item.is_spicy && (
                  <SafeIcon icon={FiFire} className="ml-2 text-orange-500" />
                )}
                {!item.is_active && (
                  <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Inaktiv</span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">ID: {item.item_id}</div>
              <div className="text-xs text-blue-500 mt-1">
                {activeProductTab==='burger' ? '🍔' : '🍕'} {activeProductTab}
              </div>
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center">
                <SafeIcon icon={FiDollarSign} className="mr-1 text-gray-500" />
                <span>{item.price.toFixed(2)} CHF</span>
              </div>
            </td>
            {item.option_type==='sauce' && (
              <td className="px-4 py-3 text-center">
                {item.is_spicy ? (
                  <SafeIcon icon={FiFire} className="mx-auto text-orange-500" />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
            )}
            <td className="px-4 py-3 text-center">
              {item.is_active ? (
                <SafeIcon icon={FiCheck} className="mx-auto text-green-500" />
              ) : (
                <SafeIcon icon={FiX} className="mx-auto text-gray-400" />
              )}
            </td>
            <td className="px-4 py-3">
              <div className="flex space-x-2">
                <button onClick={()=> handleEdit(item)} className="p-1 text-blue-600 hover:text-blue-800" title="Bearbeiten" disabled={isDeleting}>
                  <SafeIcon icon={FiEdit} />
                </button>
                <button onClick={()=> handleDelete(item)} className="p-1 text-red-600 hover:text-red-800" title="Löschen" disabled={isDeleting}>
                  <SafeIcon icon={isDeleting ? FiLoader : FiTrash2} className={isDeleting ? 'animate-spin' : ''} />
                </button>
                <button onClick={()=> handleMoveItem(item,'up')} className={`p-1 ${isFirst ? 'text-gray-300 cursor-default' : 'text-gray-600 hover:text-gray-800'}`} disabled={isFirst || isDeleting} title="Nach oben">
                  <SafeIcon icon={FiArrowUp} />
                </button>
                <button onClick={()=> handleMoveItem(item,'down')} className={`p-1 ${isLast ? 'text-gray-300 cursor-default' : 'text-gray-600 hover:text-gray-800'}`} disabled={isLast || isDeleting} title="Nach unten">
                  <SafeIcon icon={FiArrowDown} />
                </button>
              </div>
            </td>
          </>
        )}
      </tr>
    );
  };

  const renderNewItemRow=()=> {
    if (!newItem) return null;
    return (
      <tr className="bg-blue-50">
        <td className="px-4 py-3">
          <input
            type="text"
            name="name"
            value={newItem.name}
            onChange={(e)=> handleChange(e,newItem)}
            placeholder="Name der Option"
            className="w-full px-2 py-1 border border-gray-300 rounded"
            required
          />
          <input
            type="text"
            name="item_id"
            value={newItem.item_id}
            onChange={(e)=> handleChange(e,newItem)}
            placeholder="ID (optional)"
            className="w-full px-2 py-1 border border-gray-300 rounded mt-1 text-xs"
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center">
            <span className="mr-1">CHF</span>
            <input
              type="number"
              name="price"
              value={newItem.price}
              onChange={(e)=> handleChange(e,newItem)}
              step="0.1"
              min="0"
              className="w-20 px-2 py-1 border border-gray-300 rounded"
            />
          </div>
        </td>
        {newItem.option_type==='sauce' && (
          <td className="px-4 py-3 text-center">
            <input
              type="checkbox"
              name="is_spicy"
              checked={newItem.is_spicy}
              onChange={(e)=> handleChange(e,newItem)}
              className="w-5 h-5 rounded text-orange-500 focus:ring-orange-500"
            />
          </td>
        )}
        <td className="px-4 py-3 text-center">
          <input
            type="checkbox"
            name="is_active"
            checked={newItem.is_active}
            onChange={(e)=> handleChange(e,newItem)}
            className="w-5 h-5 rounded text-green-500 focus:ring-green-500"
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex space-x-2">
            <button onClick={()=> handleSave(newItem)} className="p-1 text-green-600 hover:text-green-800" disabled={!newItem.name || loading}>
              <SafeIcon icon={loading ? FiLoader : FiSave} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleCancelEdit} className="p-1 text-gray-600 hover:text-gray-800">
              <SafeIcon icon={FiX} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const categoryLabels={
    burger: {
      patty: 'Burger-Patties',
      bun: 'Brötchen',
      topping: 'Toppings',
      sauce: 'Saucen',
      side: 'Beilagen',
      spicy_level: 'Schärfegrade'
    },
    pizza: {
      base: 'Pizza-Basen',
      topping: 'Beläge',
      sauce: 'Saucen',
      side: 'Beilagen',
      spicy_level: 'Schärfegrade'
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <SafeIcon icon={FiMenu} className="mr-2 text-orange-500" /> 🍔🍕 Menü-Optionen Verwaltung
        </h1>
      </div>
      {/* Product Type Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={()=> setActiveProductTab('burger')}
          className={`py-3 px-6 text-center whitespace-nowrap ${activeProductTab==='burger' ? 'border-b-2 border-orange-500 text-orange-600 font-medium' : 'text-gray-600 hover:text-gray-800'}`}
        >
          🍔 Burger-Menü
        </button>
        <button
          onClick={()=> setActiveProductTab('pizza')}
          className={`py-3 px-6 text-center whitespace-nowrap ${activeProductTab==='pizza' ? 'border-b-2 border-orange-500 text-orange-600 font-medium' : 'text-gray-600 hover:text-gray-800'}`}
        >
          🍕 Pizza-Menü
        </button>
      </div>
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type==='success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            <SafeIcon icon={message.type==='success' ? FiCheck : FiAlertCircle} className={`mr-2 ${message.type==='success' ? 'text-green-500' : 'text-red-500'}`} />
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}
      {/* Category Tabs */}
      <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
        {getCurrentOptionTypes().map((category)=> (
          <button
            key={category}
            className={`py-2 px-4 text-center whitespace-nowrap ${activeTab===category ? 'border-b-2 border-orange-500 text-orange-600 font-medium' : 'text-gray-600 hover:text-gray-800'}`}
            onClick={()=> setActiveTab(category)}
          >
            {categoryLabels[activeProductTab][category]}
          </button>
        ))}
      </div>
      {loading && !getCurrentOptions()[activeTab]?.length ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiLoader} className="animate-spin mx-auto text-orange-500 text-xl mb-2" />
          <p className="text-gray-500">Lade {activeProductTab==='burger' ? 'Burger' : 'Pizza'}-Optionen...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preis
                  </th>
                  {activeTab==='sauce' && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scharf
                    </th>
                  )}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktiv
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getCurrentOptions()[activeTab]?.map(renderItemRow)}
                {renderNewItemRow()}
                {getCurrentOptions()[activeTab]?.length===0 && !newItem && (
                  <tr>
                    <td colSpan={activeTab==='sauce' ? 5 : 4} className="px-4 py-4 text-center text-gray-500">
                      Keine {activeProductTab==='burger' ? 'Burger' : 'Pizza'}-Optionen gefunden.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!newItem && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={()=> handleAddNew(activeTab)}
            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            disabled={loading}
          >
            <SafeIcon icon={FiPlus} className="mr-1" /> Neue {categoryLabels[activeProductTab][activeTab]}-Option hinzufügen
          </button>
        </div>
      )}
      <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
        <h3 className="font-semibold text-green-800 mb-2"> ✅ 🍔🍕 MULTI-PRODUCT MENÜ-VERWALTUNG! </h3>
        <ul className="text-sm text-green-700 space-y-1 list-disc pl-5">
          <li><strong>Separate Produkte:</strong> Burger und Pizza haben eigene Menü-Strukturen</li>
          <li><strong>Identische Funktionen:</strong> Alle Admin-Features für beide Produkte verfügbar</li>
          <li><strong>Tab-Navigation:</strong> Einfacher Wechsel zwischen Burger- und Pizza-Menü</li>
          <li><strong>Konsistente Verwaltung:</strong> Gleiche Bearbeitungs- und Löschfunktionen</li>
          <li><strong>Skalierbar:</strong> Weitere Produkte können einfach hinzugefügt werden</li>
        </ul>
      </div>
      {/* Debug Info */}
      {deletedItems.size > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-1">🔍 Debug: Gelöschte Items</h4>
          <p className="text-sm text-blue-700">
            Aktuell gelöschte Items: {Array.from(deletedItems).join(',')}
          </p>
          <button
            onClick={()=> setDeletedItems(new Set())}
            className="mt-2 text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded"
          >
            Blacklist leeren (für Tests)
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuOptionsAdmin;